import React, { createContext, useContext, useState, useEffect } from "react";
import http from "../lib/http";

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const userData = localStorage.getItem("userData");
    if (token && userData) {
      setIsAuthenticated(true);
      try {
        setUser(JSON.parse(userData));
      } catch {}
    }
    setLoading(false);
  }, []);

  const fetchProfile = async () => {
    const { data } = await http.get("/api/customer/profile");
    return data?.data || data;
  };

  // ── Login ──────────────────────────────────────────────────────────────────
  const login = async (email, password) => {
    // Throws on failure so the caller can read the real backend message
    const res = await http.post(
      "/api/customer/login",
      { email, password },
      { headers: { "Content-Type": "application/json" } }
    );

    const token = res?.data?.token;
    const profile = res?.data?.data;

    if (!token || !profile) {
      throw new Error("Invalid response from server. Please try again.");
    }

    localStorage.setItem("authToken", token);
    localStorage.setItem("userData", JSON.stringify(profile));
    localStorage.removeItem("guestToken");

    setIsAuthenticated(true);
    setUser(profile);
  };

  // ── Google Login ───────────────────────────────────────────────────────────
  const googleLogin = async (idToken) => {
    try {
      const res = await http.post(
        "/api/customer/auth/google/login",
        { id_token: idToken },
        { headers: { "Content-Type": "application/json" } }
      );

      const token = res?.data?.data?.token || res?.data?.token;
      if (!token) return false;

      localStorage.setItem("authToken", token);

      const profile = await fetchProfile();
      localStorage.setItem("userData", JSON.stringify(profile));

      setIsAuthenticated(true);
      setUser(profile);
      return true;
    } catch (error) {
      console.error("Google login error:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      return false;
    }
  };

  // ── Register ───────────────────────────────────────────────────────────────
  const register = async (payload) => {
    // payload should be a plain object, not FormData
    const res = await http.post("/api/customer/register", payload, {
      headers: { "Content-Type": "application/json" },
    });

    const token = res?.data?.token || res?.data?.data?.token;
    if (token) localStorage.setItem("authToken", token);

    return res?.data;
  };

  // ── Logout ─────────────────────────────────────────────────────────────────
  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    setIsAuthenticated(false);
    setUser(null);
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    googleLogin,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};